import type { Meta, StoryObj } from '@storybook/angular';
import { LOCALE_ID } from '@angular/core';
import { expect } from 'storybook/test';
import { CountrySelectorComponent } from '../../app/content/layout/components/atoms/country-selector/country-selector.component';
import { withCommonDecorators } from '../_utils/story-helpers';

const meta: Meta<CountrySelectorComponent> = {
  title: 'Atoms/Country Selector',
  component: CountrySelectorComponent,
  tags: ['autodocs'],
  parameters: {
    docs: {
      description: {
        component: `
The Country Selector component allows users to switch between different country versions of the Supervin website.
It displays the current country with its flag and provides a dropdown to switch to other available countries.

## Features
- Shows current country with flag icon
- Dropdown menu for switching countries
- Uses alternate links from page head metadata
- Falls back to homepage if no alternate link exists
- Fully accessible with keyboard navigation and ARIA labels

## Usage
The component reads the injected LOCALE_ID token to determine the current country.
It automatically reads alternate links from the current page's head metadata to determine available country versions.
If no alternate link is found for a target country, it redirects to that country's homepage.
        `,
      },
    },
  },
};

export default meta;
type Story = StoryObj<CountrySelectorComponent>;

/**
 * Default state showing the Danish version
 */
export const DanishVersion: Story = {
  decorators: [
    ...withCommonDecorators([
      { provide: LOCALE_ID, useValue: 'da-DK' },
    ]),
  ],
  parameters: {
    docs: {
      description: {
        story: 'Country selector as it appears on the Danish site (www.supervin.dk). Shows DK flag with dropdown to switch to SE.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-country-selector')).toBeTruthy();
  },
};

/**
 * Swedish version state
 */
export const SwedishVersion: Story = {
  decorators: [
    ...withCommonDecorators([
      { provide: LOCALE_ID, useValue: 'sv-SE' },
    ]),
  ],
  parameters: {
    docs: {
      description: {
        story: 'Country selector as it appears on the Swedish site (www.supervin.se). Shows SE flag with dropdown to switch to DK.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-country-selector')).toBeTruthy();
  },
};

/**
 * In header context showing how it integrates with the top banner
 */
export const InHeaderContext: Story = {
  decorators: [
    ...withCommonDecorators([
      { provide: LOCALE_ID, useValue: 'da-DK' },
    ]),
  ],
  render: () => ({
    template: `
      <div style="background-color: rgba(92, 10, 49, 0.8); padding: 12px 0; color: white;">
        <div style="max-width: 1200px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; padding: 0 20px;">
          <div style="display: flex; gap: 1.5rem; font-size: 0.9rem; font-weight: 700;">
            <span>🍷 Supervins unikke vingaranti</span>
            <span>💬 Kundeservice: +45 98 92 18 53</span>
            <span>🚚 Fragt fra 19,- | Fri fragt 1.000,-</span>
          </div>
          <app-country-selector></app-country-selector>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Country selector integrated into the header top banner, showing its position relative to other header elements.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-country-selector')).toBeTruthy();
  },
};
